/**
 * ReferAndEarnTile — the Dashboard's "Earn from Autonoma" tile (Part 4).
 *
 * Until the merchant has hit a discovery trigger (first AI product page
 * published, first ad campaign launched, 30 days active, or 10+
 * generation actions — GET /api/affiliate/status → discovery.eligible)
 * the tile is the shipped peer <ReferralTile>. Past a trigger — or once
 * they've applied / been approved — it becomes the two-path tile:
 *
 *   "Earn from Autonoma — refer a store or become an affiliate"
 *   [Copy my referral link] [Refer & Earn →] [Become an affiliate]
 *
 * with the affiliate state (under review / approved + this month's
 * earnings) on the right.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ReferralTile from './ReferralTile';
import {
  AFFILIATE_CHANGED_EVENT, APPLY_ROUTE, DASHBOARD_ROUTE, HUB_ROUTE, fetchHubStatus, fmtMoney,
} from '../lib/affiliate';
import { REFERRAL_CHANGED_EVENT, runShare } from '../lib/referral';

const pill = { fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 999, background: 'rgba(255,255,255,0.14)' };
const eyebrow = { fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.75)' };

export default function ReferAndEarnTile({ shop }) {
  const navigate = useNavigate();
  const [hub, setHub] = useState(null);
  const [failed, setFailed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!shop) return undefined;
    let alive = true;
    const load = (force) => fetchHubStatus({ force, stats: true }).then((h) => { if (alive) setHub(h); }).catch(() => { if (alive) setFailed(true); });
    load(false);
    const onChange = () => load(true);
    window.addEventListener(AFFILIATE_CHANGED_EVENT, onChange);
    window.addEventListener(REFERRAL_CHANGED_EVENT, onChange);
    return () => { alive = false; window.removeEventListener(AFFILIATE_CHANGED_EVENT, onChange); window.removeEventListener(REFERRAL_CHANGED_EVENT, onChange); };
  }, [shop]);

  const status = hub?.affiliate?.status || 'none';
  const eligible = !!hub?.discovery?.eligible || status === 'pending' || status === 'approved';

  // Before any trigger (or if the hub call failed): the peer-only tile.
  if (!hub || failed || !eligible) return <ReferralTile shop={shop} />;

  const copyLink = async (e) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      const { copied: ok } = await runShare('copy');
      setCopied(ok);
      if (ok) setTimeout(() => setCopied(false), 2500);
    } catch { /* the hub has every channel */ }
    setBusy(false);
  };

  const program = hub.program;
  const stats = hub.affiliate?.stats;
  const commission = program ? `${program.commissionPct}% recurring cash for ${program.commissionMonths} months` : '10% recurring cash for 6 months';

  return (
    <div
      data-testid="refer-and-earn-tile"
      role="region"
      aria-label="Earn from Autonoma"
      style={{
        position: 'relative', overflow: 'hidden', borderRadius: 16, padding: '20px 22px',
        background: 'linear-gradient(135deg, #1a1333 0%, #2b1e5e 55%, #1c2f66 100%)',
        color: 'white', boxShadow: '0 14px 40px rgba(43, 30, 94, 0.28)',
        display: 'grid', gap: 16, gridTemplateColumns: 'minmax(0, 1.6fr) minmax(240px, 1fr)', alignItems: 'center',
      }}
    >
      <div aria-hidden="true" style={{ position: 'absolute', right: -40, top: -50, width: 220, height: 220, borderRadius: '50%', background: 'radial-gradient(circle, rgba(107,78,232,0.55) 0%, rgba(107,78,232,0) 70%)' }} />
      <div style={{ position: 'relative', minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={eyebrow}>Refer &amp; Earn</span>
          <span style={pill}>Bonus credits</span>
          <span style={pill}>💸 {commission}</span>
        </div>
        <h2 style={{ margin: '8px 0 6px', fontSize: 21, lineHeight: 1.2, fontWeight: 800 }}>
          Earn from Autonoma — refer a store or become an affiliate
        </h2>
        <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5, color: 'rgba(255,255,255,0.82)' }}>
          Getting value from Autonoma? Share it and earn. Refer a store and you both get bonus credits — or join the affiliate program for {commission} when your audience signs up.
        </p>
        <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={copyLink}
            disabled={busy}
            data-testid="earn-tile-copy"
            style={{ border: 'none', borderRadius: 10, padding: '10px 16px', cursor: 'pointer', fontWeight: 800, fontSize: 14, color: '#17133a', background: 'white' }}
          >
            {copied ? '✓ Link copied' : '🔗 Copy my referral link'}
          </button>
          <button
            type="button"
            onClick={() => navigate(HUB_ROUTE)}
            data-testid="earn-tile-hub"
            style={{ border: '1.5px solid rgba(255,255,255,0.55)', background: 'transparent', color: 'white', borderRadius: 10, padding: '10px 16px', cursor: 'pointer', fontWeight: 700, fontSize: 14 }}
          >
            Refer &amp; Earn →
          </button>
          {status === 'none' && (
            <button
              type="button"
              onClick={() => navigate(APPLY_ROUTE)}
              data-testid="earn-tile-apply"
              style={{ border: '1.5px solid rgba(255,255,255,0.55)', background: 'transparent', color: 'white', borderRadius: 10, padding: '10px 16px', cursor: 'pointer', fontWeight: 700, fontSize: 14 }}
            >
              Become an affiliate
            </button>
          )}
        </div>
      </div>

      <div
        style={{ position: 'relative', background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.16)', borderRadius: 14, padding: '14px 16px', display: 'grid', gap: 8, minWidth: 0 }}
        data-testid="earn-tile-status"
      >
        {status === 'approved' ? (
          <>
            <span style={eyebrow}>Affiliate · approved</span>
            <span style={{ fontWeight: 800, fontSize: 22 }}>{fmtMoney(stats?.earnings?.month)}</span>
            <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)' }}>
              earned this month · {stats?.clicks?.month ?? '—'} clicks · {stats?.conversions?.month ?? '—'} paying
            </span>
            <button type="button" onClick={() => navigate(DASHBOARD_ROUTE)} style={{ justifySelf: 'start', border: 'none', background: 'rgba(255,255,255,0.16)', color: 'white', borderRadius: 8, padding: '7px 12px', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
              Affiliate dashboard →
            </button>
          </>
        ) : status === 'pending' ? (
          <>
            <span style={eyebrow}>Affiliate application</span>
            <span style={{ fontWeight: 700, fontSize: 14 }}>Under review — we respond within {program?.responseDays || 3} business days.</span>
            <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)' }}>Your peer referral link works in the meantime.</span>
          </>
        ) : (
          <>
            <span style={eyebrow}>Two ways to earn</span>
            <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', lineHeight: 1.45 }}>
              <strong>Peer referral</strong> — instant bonus credits, no application.<br />
              <strong>Affiliate</strong> — {commission}, for creators and agencies.
            </span>
            {hub.peer?.bonusSummary && <span style={{ fontSize: 13 }}>You hold <strong>{hub.peer.bonusSummary}</strong>.</span>}
          </>
        )}
      </div>
    </div>
  );
}
